import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Params } from '@app/common/modules/base';
import { JwtPayload } from '@app/data/auth/models';
import { AuthGuard } from '@infra/auth/auth.guard';
import { CredentialsDto } from '@infra/auth/dto/credentials.dto';
import { modal } from '@shared/alerts/modal/modal.alert';
import { JWTUtil } from '@shared/utils/jwt.util';
import { SweetAlertOptions } from 'sweetalert2';

export enum PermissaoEnum {
  INSERIR = 1,
  EDITAR = 2,
  DELETAR = 3,
  CONSULTAR = 4,
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly credentialsKey = 'credentials';

  get isAuthenticated(): boolean {
    if (this.credentials?.accessToken) {
      return !JWTUtil.isExpired(this.credentials.accessToken);
    }

    return false;
  }

  get credentials(): CredentialsDto | null {
    const credentials = localStorage.getItem(this.credentialsKey);
    return credentials ? JSON.parse(credentials) : null;
  }

  set credentials(credentials: CredentialsDto | null) {
    if (credentials) {
      localStorage.setItem(this.credentialsKey, JSON.stringify(credentials));
    } else {
      localStorage.removeItem(this.credentialsKey);
      localStorage.removeItem('clienteSelecionado');
      localStorage.removeItem('idClienteSelecionado');
    }
  }

  get accessToken(): string | null {
    return this.credentials?.accessToken || null;
  }

  get decodeToken(): JwtPayload | null {
    try {
      return JWTUtil.decode(this.accessToken!);
    } catch (err) {
      console.log('decodeToken', err);
      return null;
    }
  }

  get accessTokenLw(): string | null {
    return this.credentials?.accessTokenLw || null;
  }

  get refreshToken(): string | null {
    return this.credentials?.refreshToken || null;
  }

  get primeiroAcesso(): 'S' | 'N' | null {
    return this.credentials?.primeiroAcesso || null;
  }

  set primeiroAcesso(primeiroAcesso: 'S' | 'N' | null) {
    if (this.credentials && primeiroAcesso) {
      this.credentials = { ...this.credentials, primeiroAcesso };
    }
  }

  get usuario() {
    return this.credentials?.usuario || null;
  }

  set usuario(usuario: CredentialsDto['usuario'] | null) {
    if (this.credentials && usuario) {
      this.credentials = { ...this.credentials, usuario };
    }
  }

  get permissoes() {
    return this.usuario?.permissoes || null;
  }

  get initialRoute(): string {
    if (!this.isAuthenticated) {
      return '/logout';
    }

    const permissionFire: SweetAlertOptions = {
      icon: 'warning',
      title: 'Nenhuma permissão encontrada',
      html: 'Entre em contato com o administrador do sistema para solicitar permissões.',
    };

    if (!this.permissoes || this.permissoes.length === 0) {
      modal.fire(permissionFire);
      return '/logout';
    }

    // Buscar a primeira rota que o usuário tenha permissão de consultar
    const routes = this.router.config
      .filter((r) => r.canActivate?.includes(AuthGuard))
      .map((r) => r.path || '');
    const permissoes = this.permissoes
      .filter((p) => p.acaoId === PermissaoEnum['CONSULTAR'])
      .map((p) => p.formularioId);
    const route = routes.find((r) => permissoes.includes(r));

    if (!route) {
      modal.fire(permissionFire);
      return '/logout';
    }

    return '/' + route;
  }

  constructor(private router: Router) {
    if (!this.isAuthenticated) {
      localStorage.removeItem(this.credentialsKey);
    }
  }

  public hasPermission(formularioId: string, acao: keyof typeof PermissaoEnum): boolean {
    if (!this.isAuthenticated || !this.permissoes) {
      return false;
    }

    return this.permissoes.some(
      (permissao) =>
        permissao.formularioId === formularioId && permissao.acaoId === PermissaoEnum[acao],
    );
  }

  public injectClienteIdOnRequest(params: Params) {
    const clienteId = this.decodeToken?.clienteId;
    if (params.search === '' && clienteId) {
      params.search += `clienteId=${clienteId}`;
    } else {
      params.search += `&clienteId=${clienteId}`;
    }
    return params;
  }
}
